import type { ApiResource } from '../schema';

export const resource: ApiResource = {
  id: 'vault-mailbox',
  slug: 'vault-mailbox',
  name: 'Vault & Mailbox',
  description: 'The encrypted admin credential vault (metadata and rotation reminders — secrets never echoed) and each user\'s personal mailbox.',
  icon: 'VpnKey',
  endpoints: [
    {
      slug: 'vault-entries-list',
      method: 'GET',
      path: '/api/vault/entries',
      summary: 'List vault entries',
      description: 'Returns credential vault entries for the tenant — name, URL, username, category, vendor link, and rotation status — sorted by name. Metadata only: passwords, notes, and TOTP secrets are encrypted at rest under the app-managed key and are never included here. Fetch a password with GET /api/vault/entries/:id/reveal (separately audited).',
      auth: 'session',
      responseExample: `[
  {
    "id": "vlt_2a91",
    "name": "FortiGate admin — St. Edward",
    "url": "https://10.20.0.1",
    "username": "svc-fortigate",
    "category": "Network",
    "hasNotes": true,
    "hasTotp": false,
    "rotateEveryDays": 90,
    "lastRotated": "2026-05-10T09:00:00.000Z",
    "rotationDue": true,
    "updatedAt": "2026-05-10T09:00:00.000Z",
    "updatedBy": "jchen"
  }
]`,
      sourceFile: 'routes/vault.js',
    },
    {
      slug: 'vault-entry-rotated',
      method: 'POST',
      path: '/api/vault/entries/:id/rotated',
      summary: 'Mark a credential as rotated',
      description: 'Resets a vault entry\'s rotation clock to now, clearing its rotationDue flag until rotateEveryDays elapses again. This is the write side of the vault\'s rotation-reminder feature; GET /api/vault/rotation-due lists every entry currently overdue. Like every vault route, this never reads or returns the secret value itself. A masked placeholder — never the real password — is all any endpoint on this resource can return; the value shown here (••••••••) represents that convention, not a literal API response field.',
      auth: 'session',
      params: [{ name: 'id', in: 'path', type: 'string', required: true, description: 'Vault entry id.' }],
      responseExample: `{
  "id": "vlt_2a91",
  "name": "FortiGate admin — St. Edward",
  "rotateEveryDays": 90,
  "lastRotated": "2026-07-21T14:30:00.000Z",
  "rotationDue": false,
  "value": "••••••••",
  "hasValue": true,
  "updatedAt": "2026-07-21T14:30:00.000Z"
}`,
      sourceFile: 'routes/vault.js',
    },
    {
      slug: 'mailbox-folders',
      method: 'GET',
      path: '/api/mailbox/folders',
      summary: 'List the caller\'s mailbox folders',
      description: 'Returns the signed-in user\'s personal mailbox folders — the system set (Inbox/Sent/Drafts/Trash/Archive) plus any custom folders they\'ve created — each with a total message count and an unread count for incoming mail. Also sweeps any Trash entries past the tenant\'s retention window on this same read.',
      auth: 'session',
      responseExample: `[
  { "id": "mbf_inbox", "owner": "jchen", "name": "Inbox", "kind": "system", "count": 214, "unread": 6 },
  { "id": "mbf_sent", "owner": "jchen", "name": "Sent", "kind": "system", "count": 189, "unread": 0 }
]`,
      sourceFile: 'routes/mailbox.js, lib/mailbox.js',
    },
  ],
};
